import { Glue42Core } from "@glue42/core";

import {CallbackRegistry} from "callback-registry";
import {EmailImpl} from "./items/EmailImplementation";
import {TaskImpl} from "./items/TaskImplementation";
import {Glue42Office} from "../../glue4office";
import {concatChunkData, decodeData} from "./helpers/index";
import {TrackParams, UntrackParams} from "./types";
import {
    DisplaySecureMailMethodName,
    OnCancelMethodName,
    OnEmailReceivedMethodName,
    OnSendMethodName,
    OnTaskCreatedMethodName,
    OnTrackEmailMethodName,
    OnTrackItemMethodName,
    OnUntrackEmailMethodName,
    OnUntrackItemMethodName,
    OutlookGetAttachmentMethodName,
    OutlookGetItemAsMsgMethodName,
    SecureReplyMethodName,
} from "./helpers/methodNames";
import createItemDictionary from "./dictionaries/CreateItemDictionary";
import getAttachmentDictionary from "./dictionaries/GetAttachmentDictionary";
import chunkDataDictionary from "./dictionaries/ChunkDataDictionary";
import getItemAsMsgDictionary from "./dictionaries/GetItemAsMsgDictionary";
import trackEmailDictionary from "./dictionaries/TrackEmailDictionary";
import trackItemDictionary from "./dictionaries/TrackItemDictionary";
import {type} from "os";

const registerOnEmailReceived = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnEmailReceivedMethodName,
        (args: {email: Glue42Office.Outlook.Email}) => {
            const email = new EmailImpl(args.email, agm);
            callbacks.execute("onEmailReceived", email);
            return {email};
        });
};

const registerOnTaskCreated = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnTaskCreatedMethodName, (args: Glue42Office.Outlook.Task) => {
        const task = new TaskImpl(args, agm);
        callbacks.execute("onTaskCreated", task);
        return {task};
    });
};

const registerOnSendMethod = (agm: Glue42Core.AGM.API) => {
    return agm.register(OnSendMethodName, (args: {cookie: string, email?: Glue42Office.Outlook.T42Email, task?: Glue42Office.Outlook.T42Task}) => {
        const {cookie, email, task} = args;
        if (!createItemDictionary.containsKey(cookie) ||
            typeof createItemDictionary.get(cookie).onSent !== "function") {
            return;
        }

        if (email && !task) {
            createItemDictionary.get(cookie).onSent(new EmailImpl(email, agm));
            return {cookie, email};
        } else if (!email && task) {
            createItemDictionary.get(cookie).onSent(new TaskImpl(task, agm));
            return {cookie, task};
        }
        createItemDictionary.remove(cookie);
     });
};

const registerOnCancelMethod = (agm: Glue42Core.AGM.API) => {
    return agm.register(OnCancelMethodName, (args: {cookie: string, itemID: string}) => {
        const {cookie} = args;
        if (!createItemDictionary.containsKey(cookie) ||
            typeof createItemDictionary.get(cookie).onCanceled !== "function") {
            return;
        }

        createItemDictionary.get(cookie).onCanceled();
        createItemDictionary.remove(cookie);
        return {cookie};
    });
};

const getChunkDataSuccessCallback = (methodCallbacksDictionary: {[key: string]: any}, cookie: string) => {
    const attachmentData: string = concatChunkData(chunkDataDictionary.get(cookie));
    if (methodCallbacksDictionary.containsKey(cookie)) {
        methodCallbacksDictionary.get(cookie).resolve(decodeData(attachmentData));
    }

    chunkDataDictionary.remove(cookie);
    methodCallbacksDictionary.remove(cookie);
};

const getChunkDataErrorCallback =
    (methodCallbacksDictionary: {[key: string]: any}, cookie: string, errorMessage: string) => {
        if (methodCallbacksDictionary.containsKey(cookie)) {
            methodCallbacksDictionary.get(cookie).reject(errorMessage);
        }

        chunkDataDictionary.remove(cookie);
        methodCallbacksDictionary.remove(cookie);
    };

const executeGetDataCallback = (percent: number, cookie: string) => {
    if (percent <= 100 &&
        getAttachmentDictionary.containsKey(cookie) &&
        typeof getAttachmentDictionary.get(cookie).callback === "function") {
            getAttachmentDictionary.get(cookie).callback(percent);
    }
};

const registerGetAttachment = (agm: Glue42Core.AGM.API) => {
    let percent = 0;
    return agm.register(OutlookGetAttachmentMethodName, (args: Glue42Office.Outlook.T42Attachment) => {
        const {cookie, data, errorMessage, success, more, length, totalLength} = args;
        chunkDataDictionary.add(cookie, data);

        if (success) {
            percent += (length / totalLength) * 100;
            executeGetDataCallback(percent, cookie);
            if (!more) {
                if (percent < 100) {
                    executeGetDataCallback(100, cookie);
                }
                percent = 0;
                getChunkDataSuccessCallback(getAttachmentDictionary, cookie);
            }
        } else {
            percent = 0;
            getChunkDataErrorCallback(getAttachmentDictionary, cookie, errorMessage);
        }
        return {};
    });
};

const registerGetItemAsMsgMethod = (agm: Glue42Core.AGM.API) => {
    return agm.register(OutlookGetItemAsMsgMethodName, (args: Glue42Office.Outlook.GetEmailAsMsgCallbackParams) => {
        const {cookie, data, errorMessage, success, more} = args;
        chunkDataDictionary.add(cookie, data);

        if (success) {
            if (!more) {
                getChunkDataSuccessCallback(getItemAsMsgDictionary, cookie);
            }
        } else {
            getChunkDataErrorCallback(getItemAsMsgDictionary, cookie, errorMessage);
        }
        return {};
    });
};

export const registerCRMTrackEmail = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnTrackEmailMethodName, (args: TrackParams) => {
        const {conversationIds} = args;
        const email = new EmailImpl(args.email, agm);
        trackEmailDictionary.add(email.ids, conversationIds);
        callbacks.execute("onEmailTracked", {conversationIds, email});
        return {};
    });
};

export const registerCRMUntrackEmail = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnUntrackEmailMethodName, (args: UntrackParams) => {
        const {conversationIds, emailIds} = args;
        callbacks.execute("onEmailUntracked", {conversationIds, emailIds});
        return {};
    });
};

export const registerCRMTrackItem = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnTrackItemMethodName, (args: TrackParams) => {
        const {conversationIds, item} = args;
        trackItemDictionary.add(item.ids, conversationIds);
        callbacks.execute("onItemTracked", {conversationIds, event: item});
        return {conversationIds, event: item};
    });
};

export const registerCRMUntrackItem = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(OnUntrackItemMethodName, (args: UntrackParams) => {
        const {conversationIds, itemIds} = args;
        callbacks.execute("onItemUntracked", {conversationIds, eventIds: itemIds});
        return {conversationIds, eventIds: itemIds};
    });
};

const registerSecureReply = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(SecureReplyMethodName,
        (args: {inReplyToMail: Glue42Office.Outlook.T42Email}) => {
            const email = new EmailImpl(args.inReplyToMail, agm);
            callbacks.execute("onSecureReply", email);
            return {email};
        });
};

const registerDisplaySecureEmail = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    return agm.register(DisplaySecureMailMethodName,
        (args: {inReplyToMail: Glue42Office.Outlook.T42Email}) => {
            const email = new EmailImpl(args.inReplyToMail, agm);
            callbacks.execute("onDisplaySecureEmail", email);
            return {email};
        });
};

export const registerAgmMethods = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    registerOnEmailReceived(agm, callbacks);
    registerOnTaskCreated(agm, callbacks);
    registerOnSendMethod(agm);
    registerOnCancelMethod(agm);
    registerGetItemAsMsgMethod(agm);
    registerGetAttachment(agm);
    registerSecureReply(agm, callbacks);
    registerDisplaySecureEmail(agm, callbacks);
};
